<template>
  <div :class="`${el}__careLevel`">
    <Copy element="div" :staticClass="`${el}__careLevelHeadline`" color="primaryBlue" centered bold>
      {{ headline || $t(`careLevelChecker.box.headline${isSendable ? '0' : careLevelSituation}`) }}
    </Copy>
    <div :class="`${el}__careLevelInner`">
      <Copy element="div" centered bold>
        {{ $t('careLevelChecker.box.subline') }}
      </Copy>
      <div :class="`${el}__careLevelButtons`">
        <div
          v-for="i in 5"
          :key="`careLevel-${i}`"
          :class="{
            [`${el}__careLevelButton`]: true,
            [`${el}__careLevelButton--hasCareLevel`]: hasCareLevel || hasNoMoreCareLevel,
            [`${el}__careLevelButton--active`]: i === careLevel,
          }"
          @click="onClickCareLevel(i)"
          tabindex="0"
          @keyup.enter="onClickCareLevel(i)"
          @keyup.space="onClickCareLevel(i)"
          role="radio"
          :aria-checked="i === careLevel"
        >
          {{ i }}
        </div>
      </div>

      <div
        v-if="careLevelIsResettable"
        :class="{
          [`${el}__careLevelButtonText`]: true,
          [`${el}__careLevelButtonText--hasCareLevel`]: hasCareLevel || hasNoMoreCareLevel,
          [`${el}__careLevelButtonText--active`]: 0 === careLevel,
        }"
        @click="onClickCareLevel(0)"
        tabindex="0"
        @keyup.enter="onClickCareLevel(0)"
        @keyup.space="onClickCareLevel(0)"
        role="radio"
        :aria-checked="0 === careLevel"
      >
        <span :class="`${el}__careLevelButtonLabel`">{{ $t('careLevelChecker.box.noPG') }}</span>
      </div>

      <div
        :class="{
          [`${el}__careLevelDate`]: true,
          [`${el}__careLevelDate--visible`]: hasCareLevel || hasNoMoreCareLevel,
          [`${el}__careLevelDate--shown`]: careLevelDateShown,
        }"
      >
        <div :class="`${el}__careLevelDateInner`">
          <Copy element="div" centered>
            {{
              datepickerHeadline ||
              $t(
                `careLevelChecker.box.date.headline${
                  isSendable ? dateHeadline : careLevelSituation
                }`
              )
            }}
          </Copy>
          <Datepicker
            v-model="careLevelDate"
            name="careLevelDate"
            :language="de"
            :format="$t('datepicker.format')"
            :placeholder="$t('datepicker.placeholder')"
            :class="`${el}__careLevelDatePicker`"
            :disabledDates="disabledDates"
          />
        </div>
      </div>
    </div>
    <div
      v-if="isSendable"
      :class="`${el}__careLevelFormButton ${el}__careLevelFormButton--centered`"
    >
      <Button :label="$t('profile.button.label.cancel')" type="outlined" @click="close" small />
      <Button
        :label="$t('careLevelModal.cta')"
        :disabled="careLevel === null"
        @click="onClick"
        :loading="loading"
        :expand="!$matchMedia.s"
        small
      />
    </div>
  </div>
</template>

<script>
import { de } from 'vuejs-datepicker/dist/locale';
import Datepicker from 'vuejs-datepicker';
import Copy from '@/objects/Copy';
import Button from '@/objects/Button';

import {
  ROUTE_API_UPDATE_CARE_LEVEL_DATA,
  ROUTE_API_CLEAR_TASK_SCHEDULER,
} from '@/constants/apiRoutes';
import { HEALTH_REQUEST, HEALTH_TOGGLE_CARE_LEVEL_MODAL } from '@/store/actions/health';
import { TASKS_UPDATE_CURRENT_TASK, TASKS_REQUEST_TASK } from '@/store/actions/tasks';
import Vue from 'vue';

import { mapFieldsHealth } from '@/store/mapFields';

export default {
  name: 'CareLevelForm',
  props: {
    headline: String,
    datepickerHeadline: String,
    isSendable: {
      type: Boolean,
      default: false,
    },
    careLevelIsResettable: {
      type: Boolean,
      default: false,
    },
    controlled: {
      type: Boolean,
      default: false,
    },
    defaultCareLevel: {
      type: Number,
      default: null,
    },
    defaultCareLevelDate: {
      type: [Date, String],
      default: null,
    },
  },
  computed: {
    ...mapFieldsHealth(['careLevelSituation', 'healthUuid']),

    hasCareLevel() {
      return this.careLevel !== null && this.careLevel > 0;
    },

    hasNoMoreCareLevel() {
      return this.careLevel === 0;
    },

    dateHeadline() {
      return this.hasNoMoreCareLevel ? 2 : 0;
    },
  },
  watch: {
    careLevelDate() {
      this.$emit('onChangeCareLevelDate', this.careLevelDate);
    },

    careLevel() {
      if (this.hasCareLevel || this.hasNoMoreCareLevel) {
        this.removeHiddenOverflow();
      } else {
        this.careLevelDateShown = false;
        this.careLevelDate = null;
      }
    },
  },
  components: {
    Datepicker,
    Copy,
    Button,
  },
  data() {
    return {
      careLevel: this.defaultCareLevel,
      careLevelDate: this.defaultCareLevelDate,
      careLevelDateShown: false,
      loading: false,
      de,
      el: 'c-careLevelChecker',
      disabledDates: {
        from: new Date(Date.now()),
      },
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  mounted() {
    if (this.hasCareLevel || this.hasNoMoreCareLevel) {
      this.removeHiddenOverflow();
    }
  },
  updated() {
    if (this.controlled) {
      if (this.careLevel !== this.defaultCareLevel) {
        this.careLevel = this.defaultCareLevel;
      }
      if (this.careLevelDate !== this.defaultCareLevelDate) {
        this.careLevelDate = this.defaultCareLevelDate;
      }
    }
  },
  methods: {
    removeHiddenOverflow() {
      setTimeout(() => {
        this.careLevelDateShown = true;
      }, 400); // medium transition time
    },

    onClickCareLevel(careLevel) {
      this.careLevel = careLevel;
      this.$emit('onChangeCareLevel', careLevel);
    },

    close() {
      this.$store.dispatch(HEALTH_TOGGLE_CARE_LEVEL_MODAL);
      this.loading = false;
      this.$emit('trackingLinkClicked', 'btn-Abbrechen', 'button');
    },

    healthRequest() {
      this.$store.dispatch(HEALTH_REQUEST).then(this.close).catch(this.close);
    },

    onClick() {
      this.loading = true;

      const data = {
        personInNeedOfCareUuid: this.healthUuid,
        careLevel: this.careLevel,
        careLevelDate: this.careLevelDate,
        careLevelSituation: this.careLevel > 0 ? 0 : 2,
      };

      Vue.axios
        .post(ROUTE_API_UPDATE_CARE_LEVEL_DATA, {
          careLevelWizard: data,
        })
        .then(() => {
          if (this.careLevel === 0 && this.defaultCareLevel !== this.careLevel) {
            /**
             * Wenn "Kein Pflegegrad" ausgewählt wurde,
             * muss der Aufgabenplaner zurückgesetzt werden.
             */

            Vue.axios.post(ROUTE_API_CLEAR_TASK_SCHEDULER, { uuid: this.healthUuid }).then(() => {
              this.$store.dispatch(TASKS_REQUEST_TASK).then(this.healthRequest).catch(this.close);
            });
          } else if (this.careLevel > 0 && this.defaultCareLevel !== this.careLevel) {
            /**
             * Hier kommt man nur hin, wenn ein bestätigter Pflegegrad
             * eingetragen wird. Wenn der neue Pflegegrad vom aktuellen
             * abweicht, dann setze die nächste Aufgaben P3-20 (Pflegeleistungen)
             */

            this.$store
              .dispatch(TASKS_UPDATE_CURRENT_TASK, 'P3-20')
              .then(this.healthRequest)
              .catch(this.close);
          } else {
            this.healthRequest();
          }
          this.$emit('trackingLinkClicked', 'btn-Speichern', 'button');
        })
        .catch(this.close);
    },
  },
};
</script>
